import { useRef, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import validator from 'validator';
import { AddAPhoto as AddAPhotoIcon, Collections as CollectionsIcon } from '@mui/icons-material';
import {
  Button,
  enqueueSnackbar,
  fullScreenButtonClick,
  ImageGallery,
  ImageGalleryCard,
  ImageGalleryCardWrapper,
  Input,
  InputContainer,
  InputPhoneNumber,
  SearchableSelect,
  Select,
  Typography,
} from '../../design-system';
import { useCompleteGateOutMutation, useUpdateImagesGateOutMutation } from '../../hooks';
import {
  downloadBlob,
  extendContainerValidator,
  filesFormatValidator,
  getPhoneNumberValue,
} from '../../utils';
import { INPUT_ERRORS, MESSAGES, REGEX } from '../../const';
import { DashbaordFormFooter, DashboardInputs, DashboardWrapper, HideInput } from '../dashboard';
import { gateOutsService } from '../../services';

export const GateOutsComplete = () => {
  const completeMutation = useCompleteGateOutMutation();
  const uploadImagesMutation = useUpdateImagesGateOutMutation();

  const [containerIds, setContainerIds] = useState<string[]>([]);
  const [containerNumbers, setContainerNumbers] = useState<string[]>([]);
  const [licensePlateNumber, setLicensePlateNumber] = useState('');
  const [authorizedLicensePlateNumbers, setAuthorizedLicensePlateNumbers] = useState([]);
  const [driverFirstName, setDriverFirstName] = useState('');
  const [driverLastName, setDriverLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [comment, setComment] = useState('');

  const [images, setImages] = useState<any>([]);
  const [files, setFiles] = useState<any>([]);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const imagesInputRef = useRef<any>();
  const imagesGalleryInputRef = useRef<any>();

  const [searchBy, setSearchBy] = useState<'container' | 'licensePlateNumber' | undefined>();
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [updateKey, setUpdateKey] = useState(Math.random());

  const validators = {
    licensePlateNumber: !String(licensePlateNumber).length
      ? INPUT_ERRORS.REQUIRED.LICENSE_PLATE_NUMBER
      : !validator.isLength(licensePlateNumber, { min: 4, max: 10 })
      ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER_LENGTH
      : !REGEX.LICENSE_PLATE_NUMBER.test(licensePlateNumber)
      ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER
      : '',
    driverFirstName: !String(driverFirstName).length
      ? INPUT_ERRORS.REQUIRED.DRIVER_NAME
      : !validator.isLength(driverFirstName, { max: 30 })
      ? INPUT_ERRORS.NOT_VALID.USERNAME_LENGTH
      : !REGEX.USER_NAME.test(driverFirstName)
      ? INPUT_ERRORS.NOT_VALID.DRIVER_NAME
      : '',
    driverLastName: !String(driverLastName).length
      ? INPUT_ERRORS.REQUIRED.DRIVER_NAME
      : !validator.isLength(driverLastName, { max: 30 })
      ? INPUT_ERRORS.NOT_VALID.USERNAME_LENGTH
      : !REGEX.USER_NAME.test(driverLastName)
      ? INPUT_ERRORS.NOT_VALID.DRIVER_NAME
      : '',
    phoneNumber:
      !!phoneNumber && !isValidPhoneNumber(`+${getPhoneNumberValue(phoneNumber)}`)
        ? INPUT_ERRORS.NOT_VALID.PHONE_NUMBER
        : '',
    comment:
      !!comment && !validator.isLength(comment, { min: 10, max: 200 })
        ? INPUT_ERRORS.NOT_VALID.COMMENT_LENGTH
        : '',
  };

  const resetForm = () => {
    setContainerIds([]);
    setContainerNumbers([]);

    setLicensePlateNumber('');
    setAuthorizedLicensePlateNumbers([]);
    setDriverFirstName('');
    setDriverLastName('');
    setPhoneNumber(undefined);
    setComment('');

    setImages([]);
    setFiles([]);
    imagesInputRef.current.type = 'text';
    imagesInputRef.current.type = 'file';
    imagesGalleryInputRef.current.type = 'text';
    imagesGalleryInputRef.current.type = 'file';

    setSearchBy(undefined);
    setIsSubmitClicked(false);
    setUpdateKey(Math.random());
  };

  const handleLicensePlateNumberSelect = (option: any) => {
    option = typeof option === 'string' ? option : '';

    if (option === licensePlateNumber || !option) {
      resetForm();
    } else {
      if (searchBy === 'licensePlateNumber') {
        resetForm();
        setSearchBy('licensePlateNumber');
      }

      setLicensePlateNumber(option);
      if (!searchBy) setSearchBy('licensePlateNumber');
    }
  };

  const handleContainerSelect = (option: any, isSolo: boolean) => {
    const index = containerIds.indexOf(option.id);
    isSolo = isSolo || (containerIds.length === 0 && !licensePlateNumber);

    let newContainerIds = containerIds;
    let newContainerNumbers = containerNumbers;

    if (index !== -1) {
      newContainerIds = containerIds.filter((_, i) => i !== index);
      newContainerNumbers = containerNumbers.filter((_, i) => i !== index);
      if (isSolo) {
        resetForm();
        return;
      }
    } else if (option.id) {
      newContainerIds = isSolo ? [option.id] : [option.id, ...containerIds];
      newContainerNumbers = isSolo ? [option.value] : [option.value, ...containerNumbers];
      if (isSolo) {
        setAuthorizedLicensePlateNumbers(option.gateOut.authorizedLicensePlateNumbers);
        setLicensePlateNumber(option.gateOut.authorizedLicensePlateNumbers[0]);
        setComment(option.gateOut.comment || '');
      }
    } else {
      resetForm();
      return;
    }

    setContainerIds(newContainerIds);
    setContainerNumbers(newContainerNumbers);

    if (!searchBy) setSearchBy('container');
  };

  const handleChangeFiles = (newImages: any, newFiles: any, isCapture?: boolean) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    if (isCapture) {
      downloadBlob(newFiles[0], newFiles[0].name);
    }

    setImages([...images, ...newImages]);
    setFiles([...files, ...newFiles]);

    imagesInputRef.current.type = 'text';
    imagesInputRef.current.type = 'file';
    imagesGalleryInputRef.current.type = 'text';
    imagesGalleryInputRef.current.type = 'file';
  };

  const handleDeleteImageClick = (i: number) => {
    setImages(images.filter((_: any, index: number) => index !== i));
    setFiles(files.filter((_: any, index: number) => index !== i));
  };

  const handleZoomClick = (i: number) => {
    setZoomImageIndex(i);
    fullScreenButtonClick();
  };

  const handleSubmitClick = () => {
    setIsSubmitClicked(true);

    if (images.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    if (Object.values(validators).some((field) => !!field)) {
      return;
    }

    const data: any = {
      driver: {
        licensePlateNumber,
        firstName: driverFirstName,
        lastName: driverLastName,
        phoneNumber: getPhoneNumberValue(phoneNumber),
      },
      comment,
    };

    Object.keys(data.driver).forEach((key) => {
      if (!data.driver[key]) delete data.driver[key];
    });
    if (!Object.keys(data.driver).length) delete data.driver;
    if (!data.comment) delete data.comment;

    completeMutation.mutate(
      {
        ids: containerIds,
        props: data,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.COMPLETED, 'success');
          resetForm();
        },
        onError: () => enqueueSnackbar(MESSAGES.GATE_OUT.ERROR.NOT_COMPLETED, 'error'),
      },
    );

    uploadImagesMutation.mutate({
      ids: containerIds,
      photos: images.map((_: any, i: any) => ({
        action: 'upload',
        file: files[i],
      })),
    });
  };

  return (
    <>
      <DashboardWrapper>
        <Typography variant={'textXL'} weight={'bold'}>
          Gate Out Complete
        </Typography>
        <DashboardInputs key={updateKey}>
          {searchBy !== 'container' && (
            <>
              <SearchableSelect
                selectedOptionLabel={licensePlateNumber || 'Select License Plate Number'}
                label={'Allowed License Plate Numbers*'}
                onSelect={handleLicensePlateNumberSelect}
                queryFn={(inputValue) =>
                  gateOutsService.getLicensePlateNumber({
                    params: {
                      licensePlateNumber: inputValue,
                      status: ['LOADED', 'LOADING_ALLOWED'],
                    },
                  })
                }
                queryKey={['gate-outs', 'licensePlateNumber']}
                withClearButton={!!licensePlateNumber}
              />
              <SearchableSelect
                InputComponent={InputContainer}
                label={'Container Number*'}
                selectedOptionLabel={containerNumbers.join(', ') || 'Select Container Number'}
                onSelect={(option) => handleContainerSelect(option, false)}
                queryKey={[
                  'gate-outs',
                  searchBy === 'licensePlateNumber' ? licensePlateNumber : '',
                ]}
                inputValueKey={(inputValue: string) =>
                  extendContainerValidator(inputValue) && inputValue.length >= 3 ? inputValue : ''
                }
                queryFn={(inputValue: string) =>
                  gateOutsService.options({
                    status: ['LOADED', 'LOADING_ALLOWED'],
                    ...(extendContainerValidator(inputValue) && inputValue.length >= 3
                      ? { containerNumber: inputValue }
                      : {}),
                    ...(searchBy === 'licensePlateNumber' ? { licensePlateNumber } : {}),
                    notLoading: false,
                  })
                }
                selectedAreasEqaul={{ key: 'id', keys: containerIds }}
                multiple
              />
            </>
          )}
          {searchBy === 'container' && (
            <SearchableSelect
              InputComponent={InputContainer}
              label={'Container Number*'}
              selectedOptionLabel={containerNumbers.join(', ') || 'Select Container Number'}
              onSelect={(option) => handleContainerSelect(option, true)}
              queryKey={['gate-outs']}
              inputValueKey={(inputValue: string) =>
                extendContainerValidator(inputValue) && inputValue.length >= 3 ? inputValue : ''
              }
              queryFn={(inputValue: string) =>
                gateOutsService.options({
                  status: ['LOADED', 'LOADING_ALLOWED'],
                  ...(extendContainerValidator(inputValue) && inputValue.length >= 3
                    ? { containerNumber: inputValue }
                    : {}),
                  notLoading: false,
                })
              }
              selectedAreasEqaul={{ key: 'id', keys: containerIds }}
              withClearButton={!!containerNumbers.length}
            />
          )}
          {!!containerIds.length && (
            <>
              {searchBy === 'container' && (
                <Select
                  label={'Allowed License Plate Numbers*'}
                  options={authorizedLicensePlateNumbers}
                  value={licensePlateNumber}
                  onSelect={handleLicensePlateNumberSelect}
                  withClearButton={!!licensePlateNumber}
                />
              )}
              <Input
                label={'Driver First Name*'}
                placeholder={'Driver First Name'}
                data-size={'medium'}
                defaultValue={driverFirstName}
                onChange={(e) => setDriverFirstName(e.target.value)}
                error={isSubmitClicked && !!validators.driverFirstName}
                helperText={isSubmitClicked ? validators.driverFirstName : ''}
              />
              <Input
                label={'Driver Last Name*'}
                placeholder={'Driver Last Name'}
                data-size={'medium'}
                defaultValue={driverLastName}
                onChange={(e) => setDriverLastName(e.target.value)}
                error={isSubmitClicked && !!validators.driverLastName}
                helperText={isSubmitClicked ? validators.driverLastName : ''}
              />
              <InputPhoneNumber
                label={'Driver Phone Number'}
                placeholder={'Driver Phone Number'}
                data-size={'medium'}
                value={phoneNumber}
                defaultValue={'+998'}
                defaultCountry={'UZ'}
                onChange={(e) => setPhoneNumber(e.target.value)}
                error={isSubmitClicked && !!validators.phoneNumber}
                helperText={isSubmitClicked ? validators.phoneNumber : ''}
              />
              <Input
                data-size={'medium'}
                label={'Comment'}
                defaultValue={comment}
                onChange={(e) => setComment(e.target.value)}
                error={isSubmitClicked && !!validators.comment}
                helperText={isSubmitClicked ? validators.comment : ''}
              />
            </>
          )}
          {!!containerIds.length && (
            <DashboardInputs columns={'1fr max-content max-content'}>
              <Typography variant={'textM'} weight={'bold'}>
                Images
              </Typography>
              <Button onClick={() => imagesInputRef.current.click()} isSquare>
                <AddAPhotoIcon />
              </Button>
              <Button onClick={() => imagesGalleryInputRef.current.click()} isSquare>
                <CollectionsIcon />
              </Button>
            </DashboardInputs>
          )}
          <HideInput>
            <Input
              id={'images'}
              label={'Container Images'}
              data-size={'medium'}
              type={'file'}
              inputRef={imagesInputRef}
              inputProps={{
                accept: '.jpeg, .jpg, .png, .webp',
                multiple: true,
                capture: 'environment',
              }}
              value={undefined}
              handleChangeFiles={(newImages, newFiles) =>
                handleChangeFiles(newImages, newFiles, true)
              }
            />
            <Input
              id={'images'}
              label={'Container Images'}
              data-size={'medium'}
              type={'file'}
              inputRef={imagesGalleryInputRef}
              inputProps={{
                accept: '.jpeg, .jpg, .png, .webp',
                multiple: true,
              }}
              value={undefined}
              handleChangeFiles={handleChangeFiles}
            />
          </HideInput>
          {!!images.length && (
            <ImageGalleryCardWrapper>
              {images.map((img: any, i: any) => (
                <ImageGalleryCard
                  key={i}
                  img={img}
                  handleZoomClick={() => handleZoomClick(i)}
                  handleDeleteImageClick={() => handleDeleteImageClick(i)}
                />
              ))}
            </ImageGalleryCardWrapper>
          )}
        </DashboardInputs>
        <DashbaordFormFooter>
          <Button size={'medium'} onClick={handleSubmitClick} disabled={!containerIds.length}>
            Submit
          </Button>
        </DashbaordFormFooter>
      </DashboardWrapper>
      <ImageGallery photos={images} startIndex={zoomImageIndex} />
    </>
  );
};
